import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import { Link } from "react-router-dom";
import { discoverFarmPoints, discoverSteps } from "@/mocks/siteData";
import { usePublicFarms } from "@/hooks/usePublicCatalog";
import { farmCoverAlt } from "@/lib/imageAlt";
import { farmReopeningLabel, isFarmOpen } from "@/mocks/farms";

export default function FarmsSection() {
  const { data: farms, loading: farmsLoading } = usePublicFarms();
  const reopening = farms.filter((farm) => !isFarmOpen(farm));
  const openCount = farms.length - reopening.length;

  return (
    <section id="farms" className="sun-glaze bg-background-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="rounded-lg overflow-hidden w-full h-72 md:h-[26rem]">
              <img
                src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20a%20vibrant%20Indian%20farm%20with%20geometric%20green%20paddy%20fields%2C%20banana%20and%20coconut%20plantations%2C%20a%20small%20thatched%20farmhouse%20and%20narrow%20mud%20paths%2C%20fresh%20sunlight%2C%20stylized%20painterly%20earthy%20tones%2C%20inviting%20rural%20landscape%2C%20flooded%20with%20golden%20sunrise%20light%2C%20warm%20amber%20sunrays%20and%20soft%20glow%2C%20misty%20atmospheric%20haze%2C%20deep%20rich%20shadows%2C%20vivid%20saturated%20lush%20green%2C%20high%20contrast%20cinematic%20golden%20hour%20richness%2C%20ultra%20detailed&nocache=true&width=1200&height=900&seq=rythugaru-farm-aerial&orientation=landscape"
                width={1200}
                height={900}
                alt="Aerial view of green Indian farm fields with coconut and banana plantations"
                title="Discover farms with Rythugaru agri tourism"
                className="w-full h-full object-cover object-top"
                decoding="async"
              />
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6 order-1 lg:order-2" delay={120}>
            <div className="flex items-center gap-3 mb-5">
              <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
              <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                Discover Farms
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
              Meet the People Who Feed Us
            </h2>

            <div className="mt-7 space-y-2">
              {discoverFarmPoints.map((p) => (
                <p key={p} className="font-heading text-xl md:text-2xl text-foreground-800">
                  {p}
                </p>
              ))}
            </div>

            <p className="mt-6 text-base md:text-lg text-foreground-700 leading-relaxed">
              Rythugaru brings these stories closer to you. Explore farms based on location, crop,
              experience, season, activities, and availability.
            </p>

            <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-8">
              {discoverSteps.map((step, i) => (
                <div key={step} className="flex items-center gap-2 md:gap-3">
                  <span className="bg-primary-50 text-primary-800 text-sm font-semibold px-4 py-2 rounded-full border border-primary-200">
                    {step}
                  </span>
                  {i < discoverSteps.length - 1 && (
                    <i className="ri-arrow-right-line text-accent-500"></i>
                  )}
                </div>
              ))}
            </div>

            <p className="mt-7 text-base text-foreground-600 leading-relaxed max-w-lg">
              Find an experience that matches your curiosity and step into rural life.
            </p>

            <Link
              to="/farms"
              className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm md:text-base font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Discover Farms
              <i className="ri-arrow-right-line"></i>
            </Link>
          </Reveal>
        </div>

        {(farmsLoading || farms.length > 0) && (
          <Reveal className="mt-16 md:mt-24">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6 md:mb-8">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-px w-10 bg-accent-500"></span>
                  <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                    Farms On The Ground
                  </span>
                </div>
                <h3 className="font-heading text-2xl md:text-3xl leading-tight text-foreground-950 font-semibold">
                  Meet the farms behind the experiences
                </h3>
                <p className="mt-2 text-sm md:text-base text-foreground-600">
                  {farms.length === 0
                    ? "Loading the farms we work with…"
                    : `${openCount} taking visits now${
                        reopening.length > 0
                          ? ` · ${reopening.length} reopening soon`
                          : ""
                      }`}
                </p>
              </div>
              <Link
                to="/farms"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse every farm
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {farms.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {[0, 1, 2, 3].map((slot) => (
                  <div
                    key={slot}
                    className="h-56 rounded-lg bg-background-100 border border-background-200 animate-pulse"
                  ></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {farms.map((farm) => {
                  const isOpen = isFarmOpen(farm);
                  const reopeningLabel = farmReopeningLabel(farm, { monthOnly: true });
                  return (
                    <Link
                      key={farm.slug}
                      to={`/farms/${farm.slug}`}
                      className={`group flex flex-col bg-background-50 rounded-lg border overflow-hidden transition-colors cursor-pointer ${
                        isOpen
                          ? "border-background-200 hover:border-primary-300"
                          : "border-secondary-300 hover:border-secondary-400"
                      }`}
                    >
                      <div className="relative w-full h-40 md:h-44 overflow-hidden">
                        <img
                          src={farm.image}
                          width={1200}
                          height={900}
                          alt={farmCoverAlt(farm)}
                          title={`${farm.name} - ${farm.crop} farm in ${farm.village}`}
                          className="w-full h-full object-cover object-top"
                          decoding="async"
                        />
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-background-50/90 text-foreground-900 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                          <i className="ri-map-pin-2-line text-accent-600"></i>
                          {farm.village}
                        </span>
                        {!isOpen && (
                          <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-secondary-900/90 text-background-50 text-[11px] font-semibold px-2.5 py-1 rounded-full">
                            <i className="ri-time-line"></i>
                            Reopening soon
                          </span>
                        )}
                      </div>

                      <div className="p-4 md:p-5 flex flex-col flex-1">
                        <h4 className="font-heading text-base md:text-lg leading-snug text-foreground-950 font-semibold">
                          {farm.name}
                        </h4>
                        <p className="text-xs text-foreground-600 mt-1.5">
                          {farm.crop} · {farm.area}
                        </p>
                        {!isOpen && (
                          <p className="text-xs font-medium text-secondary-900 mt-1.5">
                            {reopeningLabel
                              ? `Expected back by ${reopeningLabel}`
                              : "Reopening soon"}
                          </p>
                        )}
                        <p
                          className={`mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${
                            isOpen
                              ? "text-primary-700 group-hover:text-primary-800"
                              : "text-secondary-800 group-hover:text-secondary-900"
                          }`}
                        >
                          {isOpen ? "View farm" : "See reopening details"}
                          <i className="ri-arrow-right-line"></i>
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </Reveal>
        )}
      </div>
    </section>
  );
}